
import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Button, Input } from '../components/ui';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserProfile } from '../types';

const EDUCATIONAL_SNIPPETS = [
  {
    category: "The Law",
    icon: "⚖️",
    title: "Paramountcy Principle",
    text: "Under Section 1 of the Children Act 1989, the child's welfare is the court's paramount consideration."
  },
  {
    category: "Strategy",
    icon: "📝",
    title: "Position Statements",
    text: "A good position statement is factual, concise, and child-focused. Avoid 'mud-slinging'."
  }
];

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isRecovering, setIsRecovering] = useState(false);
  const [isActivation, setIsActivation] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const [error, setError] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [snippetIndex, setSnippetIndex] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { refreshProfile, currentUser, loading: authLoading } = useAuth();

  // Redirect Logic based on device type
  useEffect(() => {
    if (!authLoading && currentUser) {
      const rawUrl = window.location.href;
      if (rawUrl.includes('success=true')) {
        navigate('/payment-success');
      } else {
        navigate('/home');
      }
    }
  }, [currentUser, authLoading, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    const modeParam = params.get('mode');

    if (modeParam === 'activation' && emailParam) {
      setIsRecovering(true);
      setIsActivation(true);
      setEmail(emailParam);
    }
  }, [location]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSnippetIndex((prev) => (prev + 1) % EDUCATIONAL_SNIPPETS.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setRecoveryMessage('');
    if (!email) { setError("Please enter your email address."); return; }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setRecoveryMessage(isActivation ? "Activation link sent! Check your email." : "Password reset email sent!");
    } catch (err: any) {
      setError("No account found or invalid email.");
    } finally { setLoading(false); }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        if (!name || !email || !password) throw new Error("Please complete all required fields.");

        let userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const userData: UserProfile = {
          uid: user.uid,
          email: user.email!,
          displayName: name,
          role: 'parent',
          subscriptionStatus: 'inactive',
          isPaid: false,
          progress: {},
          createdAt: serverTimestamp()
        };

        await setDoc(doc(db, 'users', user.uid), userData);

        try {
          await addDoc(collection(db, 'mail'), {
            to: [email],
            message: {
              subject: `Welcome to the Hub – You've taken a massive step`,
              text: `Hi ${name}, welcome to the Dads Matter Training Hub. Your free account is active. Access the Hub: ${window.location.origin}/#/hub`,
              html: `
                        <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #f8fafc; padding: 20px;">
                            <div style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.05);">
                                <div style="background-color: #4f46e5; padding: 30px; text-align: center;">
                                    <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: bold;">Dads Matter Hub</h1>
                                </div>

                                <div style="padding: 40px;">
                                    <h2 style="color: #1e293b; margin-top: 0; font-size: 22px;">Welcome, ${name}.</h2>
                                    <p style="color: #475569; font-size: 16px; line-height: 1.6;">You have taken a significant step today towards protecting your relationship with your children. Your free community account is now active.</p>
                                    
                                    <div style="margin: 30px 0; background-color: #f1f5f9; padding: 20px; border-radius: 8px;">
                                        <p style="margin: 0 0 10px 0; font-weight: bold; color: #1e293b; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">What you can access now:</p>
                                        <ul style="color: #475569; font-size: 14px; margin: 0; padding-left: 20px;">
                                            <li>Real-time Peer Support in the Dads Hub</li>
                                            <li>Anonymous Chat & Tactical Advice</li>
                                            <li>A community of fathers who "get it"</li>
                                        </ul>
                                    </div>

                                    <div style="margin: 30px 0; border: 2px dashed #e2e8f0; padding: 20px; border-radius: 8px;">
                                        <p style="margin: 0 0 10px 0; font-weight: bold; color: #4f46e5; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">Ready for the full Roadmap?</p>
                                        <p style="color: #475569; font-size: 14px; line-height: 1.5; margin-bottom: 15px;">Upgrade to <strong>Parent PRO</strong> at any time for just £25 to unlock 20+ training modules, legal statement templates, and verified certificates.</p>
                                        <a href="${window.location.origin}/#/subscription" style="color: #4f46e5; font-weight: bold; text-decoration: none; font-size: 14px;">View Parent PRO features →</a>
                                    </div>

                                    <div style="text-align: center; margin: 40px 0;">
                                        <a href="${window.location.origin}/#/hub" style="background-color: #4f46e5; color: #ffffff; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; display: inline-block;">Enter the Dads Hub</a>
                                    </div>
                                    
                                    <p style="font-size: 14px; color: #94a3b8; text-align: center;">We're glad to have you with us. You've got this.</p>
                                </div>
                                
                                <div style="background-color: #f8fafc; padding: 20px; text-align: center; border-top: 1px solid #e2e8f0;">
                                  <p style="color: #94a3b8; font-size: 12px; margin: 0;">&copy; ${new Date().getFullYear()} Dads Matter Hub.</p>
                                </div>
                            </div>
                        </div>
                    `
            }
          });
        } catch (emailErr) { }

        await refreshProfile();

        // Final login redirect check
        navigate('/home');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentSnippet = EDUCATIONAL_SNIPPETS[snippetIndex];

  const ComparisonTable = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* FREE COLUMN */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5 backdrop-blur-sm">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-emerald-400 font-black text-[10px] uppercase tracking-widest">Community</p>
              <h4 className="text-2xl font-bold text-white">FREE</h4>
            </div>
            <span className="text-2xl">💬</span>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start gap-2 text-xs text-slate-300 font-medium">
              <span className="text-emerald-400 text-sm">✓</span>
              Dads Hub Live Chat
            </li>
            <li className="flex items-start gap-2 text-xs text-slate-300 font-medium">
              <span className="text-emerald-400 text-sm">✓</span>
              Real-time Peer Support
            </li>
            <li className="flex items-start gap-2 text-xs text-slate-300 font-medium">
              <span className="text-emerald-400 text-sm">✓</span>
              Advice from your peers
            </li>
            <li className="flex items-start gap-2 text-xs text-slate-300 font-medium">
              <span className="text-emerald-400 text-sm">✓</span>
              Completely Anonymous
            </li>
          </ul>
        </div>

        {/* PAID COLUMN */}
        <div className="bg-indigo-600/20 border border-indigo-400/30 rounded-2xl p-5 backdrop-blur-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 bg-indigo-500 text-[8px] font-black px-2 py-1 uppercase rounded-bl-lg">Recommended</div>
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-indigo-300 font-black text-[10px] uppercase tracking-widest">Upgrade</p>
              <h4 className="text-2xl font-bold text-white">Parent PRO</h4>
            </div>
            <span className="text-2xl">🎓</span>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start gap-2 text-xs text-white font-bold">
              <span className="text-indigo-400 text-sm">★</span>
              20+ Training Modules
            </li>
            <li className="flex items-start gap-2 text-xs text-white font-bold">
              <span className="text-indigo-400 text-sm">★</span>
              Document Templates
            </li>
            <li className="flex items-start gap-2 text-xs text-white font-bold">
              <span className="text-indigo-400 text-sm">★</span>
              Step-by-Step Roadmap
            </li>
            <li className="flex items-start gap-2 text-xs text-white font-bold">
              <span className="text-indigo-400 text-sm">★</span>
              Live Expert Seminars
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-white/10">
            <span className="text-white font-black text-lg">£25</span>
            <span className="text-indigo-300 text-[10px] ml-1">/year</span>
          </div>
        </div>
      </div>
      <p className="text-center text-[10px] text-slate-500 italic">No credit card required for free access. Upgrade to Parent PRO at any time.</p>
    </div>
  );

  return (
    <div className="h-screen w-full flex flex-col md:flex-row bg-white overflow-hidden">
      <div className="w-full md:w-1/2 lg:w-5/12 h-full flex flex-col overflow-y-auto bg-white z-10">
        <div className="flex-1 flex flex-col justify-center p-8 md:p-12 lg:p-16 animate-slide-up max-w-xl mx-auto w-full">
          <div className="mb-6">
            <div className="mb-6"><img src="/newlogo.png?v=2" alt="Dads Matter Hub" className="h-14 w-auto rounded-2xl shadow-sm" /></div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">{isRecovering ? (isActivation ? "Activate Account" : "Reset Password") : (isLogin ? "Welcome Back" : "Create Account")}</h2>
            <p className="text-slate-500 text-sm">{isLogin ? "Continue your journey." : "Join the community for free today."}</p>
          </div>

          {!isLogin && !isRecovering && (
            <div className="md:hidden mb-8 space-y-3">
              <div className="bg-slate-900 rounded-2xl p-4 text-white shadow-xl ring-1 ring-white/10">
                <div className="flex justify-between items-center mb-3">
                  <span className="bg-emerald-500 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest text-white">Hub Access</span>
                  <span className="text-xl font-bold">FREE</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-tight">Access Peer Support, Live Chat, and advice from your peers immediately upon signup.</p>
              </div>
              <div className="bg-indigo-600 rounded-2xl p-4 text-white shadow-xl ring-4 ring-indigo-50">
                <div className="flex justify-between items-center mb-3">
                  <span className="bg-white/20 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest text-white">Full Roadmap</span>
                  <span className="text-xl font-bold">Parent PRO</span>
                </div>
                <p className="text-[10px] text-indigo-100 leading-tight">Unlock 20+ roadmap modules, document templates, and live seminars for £25.</p>
              </div>
            </div>
          )}

          {error && <div className="mb-4 p-3 bg-rose-50 text-rose-700 text-xs rounded-xl border border-rose-100 flex items-start gap-2 font-bold animate-shake">⚠️ <p>{error}</p></div>}
          {recoveryMessage && <div className="mb-4 p-3 bg-emerald-50 text-emerald-700 text-xs rounded-xl border border-emerald-100 flex items-start gap-2 font-bold">✅ <p>{recoveryMessage}</p></div>}

          {isRecovering ? (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <Input label="Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required readOnly={isActivation} />
              <Button type="submit" className="w-full !py-3.5 shadow-lg shadow-indigo-100" disabled={loading}>{loading ? "Sending..." : "Send Link"}</Button>
              <div className="mt-4 text-center"><button type="button" onClick={() => setIsRecovering(false)} className="text-slate-500 text-sm hover:text-indigo-600 font-medium transition-colors">← Back to Sign In</button></div>
            </form>
          ) : (
            <form onSubmit={handleAuth} className="space-y-4">
              {!isLogin && <Input label="Full Name" placeholder="Your Full Name" value={name} onChange={e => setName(e.target.value)} required />}
              <div className="space-y-3">
                <Input label="Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required />
                <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              {isLogin && <div className="flex justify-end"><button type="button" onClick={() => setIsRecovering(true)} className="text-xs font-bold text-indigo-600 hover:underline">Forgot Password?</button></div>}
              <Button type="submit" className={`w-full !py-3.5 shadow-xl transition-all ${isLogin ? 'shadow-indigo-100' : 'shadow-emerald-100 !bg-emerald-600 hover:!bg-emerald-700'}`} disabled={loading}>
                {loading ? "Processing..." : (isLogin ? "Sign In" : "Join Hub For Free")}
              </Button>
            </form>
          )}

          {!isRecovering && (
            <div className="mt-8 text-center pt-4 border-t border-slate-100">
              <p className="text-slate-500 text-sm">{isLogin ? "First time here? " : "Already registered? "}
                <button onClick={() => setIsLogin(!isLogin)} className="text-indigo-600 font-black hover:underline ml-1">{isLogin ? "Join Hub Free" : "Sign In"}</button>
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:flex w-1/2 lg:w-7/12 bg-slate-900 relative overflow-hidden flex-col justify-center p-12 lg:p-16 text-white h-full">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-600/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative z-10 max-w-2xl mx-auto w-full space-y-10">
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">New: Free Hub Access Enabled</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-heading font-extrabold text-white mb-4 leading-tight">Everything you need to <span className="text-indigo-400 font-outline-2">Protect</span> your kids.</h3>
            <p className="text-slate-400 text-lg mb-8 max-w-lg leading-relaxed">Join thousands of fathers mastering the legal language, strategy, and mindset required for family court success.</p>

            <ComparisonTable />
          </div>

          <div className="bg-slate-800/40 rounded-3xl p-6 border border-white/5 backdrop-blur-md shadow-2xl animate-slide-up delay-200">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 flex items-center justify-center text-3xl shadow-inner">{currentSnippet.icon}</div>
              <div className="flex-1">
                <p className="text-indigo-300 text-[10px] font-black uppercase tracking-tighter mb-1">{currentSnippet.category} Masterclass</p>
                <p className="text-slate-100 text-sm leading-relaxed italic font-medium">"{currentSnippet.text}"</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-8 opacity-50 grayscale hover:grayscale-0 transition-all duration-700">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Built for Separated Dads</div>
            <div className="flex gap-4">
              <div className="h-6 w-px bg-slate-800"></div>
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Secure • Private • Professional</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
